import { workspaceApi } from "@/api/workspace-api";
import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  BusinessModels,
  RevenueModels,
  CountryOptions,
  DistinctionOptions,
  StageOptions,
  GICSSubIndustries,
  getStageText,
  cn,
} from "@/lib/utils";
import { VirtualizedCombobox } from "@/components/ui/virtualized-combobox";
import {
  CURRENCIES,
  DEFAULT_CURRENCY,
  getCurrencyByCode,
} from "@/lib/constants/currencies";
import {
  Check,
  ChevronsUpDown,
  ChevronUp,
  Loader2,
  Pencil,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import {
  addCriterion,
  removeCriterion,
  sanitizeCriteria,
} from "./thesis-criteria";
import { useState, useMemo, useEffect } from "react";
import posthog from "posthog-js";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { useUser } from "@clerk/clerk-react";
import type { IWorkspace } from "@/types/data-models";

/**
 * A chip list with an add-box. Enter adds, so the common case of typing
 * several criteria in a row never touches the mouse.
 */
function CriteriaEditor({
  label,
  description,
  placeholder,
  values,
  onChange,
  tone,
}: {
  label: string;
  description: string;
  placeholder: string;
  values: string[];
  onChange: (next: string[]) => void;
  tone: "positive" | "negative";
}) {
  const [draft, setDraft] = useState("");

  const commit = () => {
    const next = addCriterion(values, draft);
    if (next !== values) onChange(next);
    setDraft("");
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <p className="text-xs text-muted-foreground">{description}</p>
      {values.length > 0 && (
        <ul className="flex flex-wrap gap-2 pt-1">
          {values.map((value, index) => (
            <li
              key={value}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm",
                tone === "negative"
                  ? "border-destructive/30 bg-destructive/5 text-foreground"
                  : "border-border bg-muted/40 text-foreground"
              )}
            >
              <span>{value}</span>
              <button
                type="button"
                onClick={() => onChange(removeCriterion(values, index))}
                aria-label={`Remove ${value}`}
                className="rounded-full text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Otherwise Enter submits the surrounding settings form.
              e.preventDefault();
              commit();
            }
          }}
          placeholder={placeholder}
          aria-label={label}
        />
        <Button
          type="button"
          variant="outline"
          onClick={commit}
          disabled={!draft.trim()}
          className="shrink-0 gap-1"
        >
          <Plus className="h-4 w-4" />
          Add
        </Button>
      </div>
    </div>
  );
}

export default function ThesisSettings() {
  const {
    workspace,
    isLoading,
    activeWorkspaceId,
    updateWorkspaceMutation,
    invalidateWorkspace,
  } = useWorkspaceSettings();
  const { user } = useUser();

  const [thesisContext, setThesisContext] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [minChequeSize, setMinChequeSize] = useState(0);
  const [maxChequeSize, setMaxChequeSize] = useState(0);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isInputCollapsed, setIsInputCollapsed] = useState(false);
  const [selectedIndustryTags, setSelectedIndustryTags] = useState<string[]>(
    []
  );
  const [inclusions, setInclusions] = useState<string[]>([]);
  const [exclusions, setExclusions] = useState<string[]>([]);

  useEffect(() => {
    if (workspace) {
      // The thesis is the investor's own text and the input to re-analysis, so
      // it seeds the box rather than leaving it blank. A stored thesis also
      // means the criteria below were derived from something, which is what
      // the collapsed state is telling the user.
      setThesisContext(workspace.investment_thesis || "");
      setIsInputCollapsed(Boolean(workspace.investment_thesis));
      setAdditionalInfo(workspace.additional_info || "");
      setMinChequeSize(workspace.min_cheque_size || 0);
      setMaxChequeSize(workspace.max_cheque_size || 0);
      setCurrency(workspace.currency || DEFAULT_CURRENCY);
      setSelectedIndustryTags(workspace.target_gics_sub_industries || []);
      setInclusions(sanitizeCriteria(workspace.thesis_inclusions));
      setExclusions(sanitizeCriteria(workspace.thesis_exclusions));
    }
  }, [workspace]);

  // Cheque-size fields display the amount with the selected currency's symbol
  // and thousands separators (e.g. "$1,000,000") while storing the raw number.
  // Parsing digits on change also avoids the controlled number-input leading
  // zero glitch (e.g. "01000000").
  const selectedCurrency = getCurrencyByCode(currency);
  const currencySymbol = selectedCurrency?.symbol ?? "$";
  const formatCheque = (n: number) =>
    n > 0 ? `${currencySymbol}${n.toLocaleString("en-US")}` : "";
  const parseCheque = (s: string) => Number(s.replace(/[^0-9]/g, "")) || 0;

  const industryTagOptions = useMemo(
    () => GICSSubIndustries.map((si) => ({ value: si.code, label: si.name })),
    []
  );

  const PLACEHOLDER_VALUES = [
    "no information provided",
    "not applicable",
    "n/a",
    "none",
    "",
  ];
  const filterPlaceholders = (values: string[] | undefined): string[] => {
    if (!values) return [];
    return values.filter(
      (v) => !PLACEHOLDER_VALUES.includes(v.toLowerCase().trim())
    );
  };

  const createValueChangeHandler = (field: keyof IWorkspace) => {
    return (values: string[]) => {
      if (workspace) {
        (workspace as unknown as Record<string, unknown>)[field] = values;
      }
    };
  };

  const handleAnalyzeThesis = async () => {
    if (!thesisContext.trim()) {
      toast.error("Please enter your investment thesis");
      return;
    }
    if (!activeWorkspaceId) {
      toast.error("No workspace selected");
      return;
    }
    posthog.capture("thesis_settings_updated", {
      action: "analyze",
      workspace_id: activeWorkspaceId,
      user_id: user?.id,
    });
    setIsGenerating(true);
    try {
      const data = await workspaceApi.generateClientProfile(
        activeWorkspaceId,
        thesisContext
      );
      if (data.status === "success" && data.data?.client_profile) {
        invalidateWorkspace();
        setIsInputCollapsed(true);
        toast.success("Thesis analyzed successfully!");
      } else {
        toast.error(data.message || "Failed to analyze thesis");
      }
    } catch (error) {
      console.error("Error analyzing thesis:", error);
      toast.error("Failed to analyze thesis");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    posthog.capture("thesis_settings_updated", {
      action: "save",
      workspace_id: activeWorkspaceId,
      user_id: user?.id,
    });
    const derivedIndustries = [
      ...new Set(selectedIndustryTags.map((code) => code.substring(0, 8))),
    ];
    const toastId = toast.loading("Updating settings...");
    updateWorkspaceMutation.mutate(
      {
        target_gics_industries: derivedIndustries,
        target_gics_sub_industries: selectedIndustryTags,
        target_model_companies: filterPlaceholders(
          workspace?.target_model_companies
        ),
        target_revenue_models: filterPlaceholders(
          workspace?.target_revenue_models
        ),
        target_distinctions: filterPlaceholders(
          workspace?.target_distinctions
        ),
        // Accept legacy numeric entries (workspaces persisted before the
        // string-stage migration) and normalise to canonical labels via
        // getStageText so the user's prior thesis survives the save.
        target_stage_companies: (workspace?.target_stage_companies || [])
          .map((v) => getStageText(v as string | number))
          .filter((v) => v !== "Unknown"),
        geographical_location: filterPlaceholders(
          workspace?.geographical_location
        ),
        investment_thesis: thesisContext,
        additional_info: additionalInfo,
        thesis_inclusions: inclusions,
        thesis_exclusions: exclusions,
        min_cheque_size: minChequeSize,
        max_cheque_size: maxChequeSize,
        currency,
      },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
          posthog.capture("settings_workspace_thesis_fit_updated", {
            workspace_id: activeWorkspaceId,
          });
        },
        onError: () => {
          toast.dismiss(toastId);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="mx-auto max-w-2xl px-6 py-8">
        <h1 className="text-lg font-semibold text-foreground">Thesis</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Your investment philosophy. The AI uses this as context when screening
          deals.
        </p>

        {/* Thesis Input */}
        <section className="mt-8">
          <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
            Investment Thesis
          </h2>
          <div className="rounded-lg border border-border bg-card p-4">
            <Collapsible
              open={!isInputCollapsed}
              onOpenChange={(open) => setIsInputCollapsed(!open)}
            >
              {isInputCollapsed ? (
                <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span>Thesis analyzed — criteria populated below</span>
                  </div>
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-2"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit & Re-analyze
                    </Button>
                  </CollapsibleTrigger>
                </div>
              ) : null}

              <CollapsibleContent>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Investment Thesis</Label>
                    <Textarea
                      value={thesisContext}
                      onChange={(e) => setThesisContext(e.target.value)}
                      placeholder="Describe your investment thesis, paste a URL, or combine both..."
                      className="min-h-[120px]"
                      disabled={isGenerating}
                    />
                    <p className="text-xs text-muted-foreground">
                      You can write your thesis, paste a URL, or combine both.
                      Our AI will analyze everything.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      onClick={handleAnalyzeThesis}
                      disabled={isGenerating || !thesisContext.trim()}
                      className="gap-2"
                    >
                      {isGenerating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      {isGenerating ? "Analyzing..." : "Analyze Thesis"}
                    </Button>
                    {isInputCollapsed && (
                      <CollapsibleTrigger asChild>
                        <Button type="button" variant="ghost" size="sm">
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                      </CollapsibleTrigger>
                    )}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </section>

        {/* AI Summary */}
        {additionalInfo && (
          <section className="mt-8">
            <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
              Thesis Summary
            </h2>
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-xs text-muted-foreground">
                  AI-generated summary — edit as needed
                </span>
              </div>
              <Textarea
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder="Thesis summary will appear here after analysis..."
                className="min-h-[100px]"
              />
            </div>
          </section>
        )}

        {/* Screening Criteria */}
        <section className="mt-8">
          <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
            Screening Criteria
          </h2>
          <div className="space-y-4 rounded-lg border border-border bg-card p-4">
            <div className="space-y-2">
              <Label>Target Industry Tags</Label>
              <VirtualizedCombobox
                options={industryTagOptions}
                selected={selectedIndustryTags}
                onToggle={(code) => {
                  setSelectedIndustryTags((prev) =>
                    prev.includes(code)
                      ? prev.filter((t) => t !== code)
                      : [...prev, code]
                  );
                }}
                placeholder="Select industry tags"
                searchPlaceholder="Search industry tags..."
                multiple
              />
            </div>

            <div className="space-y-2">
              <Label>Target Business Models</Label>
              <MultiSelect
                key={`bm-${workspace?.target_model_companies?.join(",") || "e"}`}
                id="fit-score-business-models"
                options={BusinessModels}
                defaultValue={filterPlaceholders(
                  workspace?.target_model_companies
                )}
                onValueChange={createValueChangeHandler(
                  "target_model_companies"
                )}
                placeholder="Select business models"
                maxCount={20}
              />
            </div>

            <div className="space-y-2">
              <Label>Target Revenue Models</Label>
              <MultiSelect
                key={`rm-${workspace?.target_revenue_models?.join(",") || "e"}`}
                id="fit-score-revenue-models"
                options={RevenueModels}
                defaultValue={filterPlaceholders(
                  workspace?.target_revenue_models
                )}
                onValueChange={createValueChangeHandler(
                  "target_revenue_models"
                )}
                placeholder="Select revenue models"
                maxCount={20}
              />
            </div>

            <div className="space-y-2">
              <Label>Target Diversity Components</Label>
              <MultiSelect
                id="fit-score-diversity"
                options={DistinctionOptions.map((d) => ({
                  label: d.text,
                  value: d.value,
                }))}
                defaultValue={filterPlaceholders(
                  workspace?.target_distinctions
                )}
                onValueChange={createValueChangeHandler("target_distinctions")}
                exclusiveValues={["Not Applicable"]}
                placeholder="Select diversity components"
                maxCount={20}
              />
            </div>

            <div className="space-y-2">
              <Label>Target Company Stages</Label>
              <MultiSelect
                key={`st-${workspace?.target_stage_companies?.join(",") || "e"}`}
                id="fit-score-stages"
                options={StageOptions.map((o) => ({
                  label: o.label,
                  value: o.value,
                }))}
                defaultValue={filterPlaceholders(
                  workspace?.target_stage_companies?.map((v) => getStageText(v))
                )}
                onValueChange={(values) => {
                  if (workspace) {
                    workspace.target_stage_companies = values;
                  }
                }}
                placeholder="Select company stages"
                maxCount={20}
              />
            </div>

            <div className="space-y-2">
              <Label>Target Geographical Locations</Label>
              <MultiSelect
                key={`loc-${workspace?.geographical_location?.join(",") || "e"}`}
                id="fit-score-locations"
                options={CountryOptions}
                defaultValue={filterPlaceholders(
                  workspace?.geographical_location
                )}
                onValueChange={createValueChangeHandler(
                  "geographical_location"
                )}
                placeholder="Select locations"
                maxCount={20}
              />
            </div>
          </div>
        </section>

        {/* Positive / negative criteria */}
        <section className="mt-8">
          <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
            Inclusions &amp; Exclusions
          </h2>
          <div className="space-y-6 rounded-lg border border-border bg-card p-4">
            <CriteriaEditor
              label="We look for"
              description="Criteria the fields above can't express — team shape, traction, product characteristics."
              placeholder="e.g. Technical founding team"
              values={inclusions}
              onChange={setInclusions}
              tone="positive"
            />
            <CriteriaEditor
              label="We don't invest in"
              description="Hard exclusions — what's out of scope regardless of how good the deal looks."
              placeholder="e.g. Crypto"
              values={exclusions}
              onChange={setExclusions}
              tone="negative"
            />
          </div>
        </section>

        {/* Cheque Size */}
        <section className="mt-8">
          <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
            Investment Size
          </h2>
          <div className="space-y-4 rounded-lg border border-border bg-card p-4">
            <div className="space-y-2">
              <Label>Currency</Label>
              <Popover open={currencyOpen} onOpenChange={setCurrencyOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={currencyOpen}
                    className="w-full justify-between font-normal"
                  >
                    {selectedCurrency
                      ? `${selectedCurrency.symbol} ${selectedCurrency.code} - ${selectedCurrency.name}`
                      : "Select currency"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[--radix-popover-trigger-width] p-0"
                  align="start"
                >
                  <Command>
                    <CommandInput placeholder="Search currency..." />
                    <CommandList>
                      <CommandEmpty>No currency found.</CommandEmpty>
                      <CommandGroup>
                        {CURRENCIES.map((c) => (
                          <CommandItem
                            key={c.code}
                            value={`${c.code} ${c.name} ${c.symbol}`}
                            onSelect={() => {
                              setCurrency(c.code);
                              setCurrencyOpen(false);
                            }}
                            className="cursor-pointer"
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0",
                                currency === c.code
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            <span className="truncate">
                              {c.symbol} {c.code} - {c.name}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minimum Cheque Size</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formatCheque(minChequeSize)}
                  onChange={(e) => setMinChequeSize(parseCheque(e.target.value))}
                  placeholder="Enter minimum"
                />
              </div>
              <div className="space-y-2">
                <Label>Maximum Cheque Size</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formatCheque(maxChequeSize)}
                  onChange={(e) => setMaxChequeSize(parseCheque(e.target.value))}
                  placeholder="Enter maximum"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="mt-8 flex justify-end">
          <Button type="submit" disabled={isGenerating}>
            Save Changes
          </Button>
        </div>
      </div>
    </form>
  );
}
